import { useMemo } from 'react';

import dotize from 'dotize';
import { CircleAlertIcon, CircleCheckIcon, DownloadIcon, UsersRoundIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Campaign, CampaignSubscriber } from '@/shared/types/campaigns';

import { DEBT_SIDEBAR_DISPLAY_VARIABLES } from '../../../constants';
import { useCampaignSubscribers } from '../../../hooks/useCampaignSubscribers';
import type { AudienceStats, DraftCampaignSubscriber } from '../../../redux/actions';
import {
  selectCsvMappings,
  selectDraftAudienceStats,
  selectDraftSubscribers,
} from '../../../redux/selectors';
import { VariableDefinition } from '../../../types/column-mapping';
import { requiresResidenceState } from '../../../utils';

/**
 * Get the variable name mapped from subscriber data
 * Handles special cases like contact_info (email/phone)
 */
const getVariablePresence = (
  variable: VariableDefinition,
  subscriberKeys: Set<string>
): boolean => {
  // Special case: contact_info matches if either email or phone exists
  if (variable.name === 'contact_info') {
    return subscriberKeys.has('email') || subscriberKeys.has('phone');
  }

  return subscriberKeys.has(variable.name);
};

interface AudienceSummarySidebarProps {
  campaign?: Campaign;
  hasUserDeletedAudience?: boolean;
  onMatchMissingFields?: () => void;
  onReviewAudience?: () => void;
  onDownloadSample?: () => void;
}

/** Format number as USD currency */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Displays audience summary after CSV upload
 * Shows total count and which required fields are matched/missing
 * Matches Figma design: node-id=2139-7811
 */
export const AudienceSummarySidebar = ({
  campaign,
  hasUserDeletedAudience = false,
  onMatchMissingFields,
  onReviewAudience,
  onDownloadSample,
}: AudienceSummarySidebarProps) => {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const isDraft = !campaignId || campaign?.status === 'draft';
  const subscribers = useSelector(selectDraftSubscribers);
  const csvMappings = useSelector(selectCsvMappings);
  const draftAudienceStats = useSelector(selectDraftAudienceStats);
  // After refresh the Redux draft is empty; pull the saved subscribers from
  // the API so the field-status checkmarks reflect what was actually saved.
  const { campaign_subscribers: apiSubscribers } = useCampaignSubscribers(campaignId);

  // Get audience stats from campaign metadata for saved campaigns
  // Use campaign.subscribers_count as source of truth for total count (NOT metadata)
  const metadata = campaign?.metadata;
  const subscriberCount = campaign?.subscribers_count ?? 0;
  const savedAudienceStats: AudienceStats | undefined =
    metadata?.email_subscribers !== undefined
      ? {
          email_subscribers: metadata.email_subscribers as number,
          phone_subscribers: metadata.phone_subscribers as number,
          total_amount_due: metadata.total_amount_due as number,
          variables_count: metadata.variables_count as number,
        }
      : undefined;

  // Prefer draft stats (from CSV upload / inline remap) over saved campaign metadata
  const audienceStats = draftAudienceStats ?? savedAudienceStats;
  const hasAudienceStats = !!(audienceStats && (subscribers.length > 0 || subscriberCount > 0));

  // Get unique keys from all subscribers (non-empty values only). Slotted
  // multi-debt keys (`debt.<N>.<field>`) are folded back to the canonical
  // `debt.<field>` so the Required fields panel checkmarks Amount / Currency /
  // etc. as filled whenever any debt slot carries the value — slot 0 is the
  // "main" debt by convention, additional slots are extra debts.
  const subscriberKeys = useMemo(() => {
    // Use the API subscribers when Redux's draft slice is empty (e.g. post-
    // refresh) so the checkmarks reflect what's actually saved.
    const source: Array<DraftCampaignSubscriber | CampaignSubscriber> =
      subscribers.length > 0 ? subscribers : (apiSubscribers ?? []);
    const keys = new Set<string>();

    source.forEach((subscriber) => {
      if (subscriber.phone) {
        keys.add('phone');
      }
      if (subscriber.email) {
        keys.add('email');
      }

      if ('context' in subscriber && subscriber.context) {
        // API shape — `context: { debts: [...], user: {...}, ... }`. Flatten
        // it so `debts.0.amount` etc. show up, then fold any indexed-debt
        // key back to the canonical `debt.<field>` form the sidebar checks.
        const flattened = dotize.convert(subscriber.context);
        Object.entries(flattened).forEach(([key, value]) => {
          if (!value) {
            return;
          }
          // dotize emits `debts[0].amount` (bracket form) for arrays; fold
          // any index back to the canonical unindexed `debt.<field>` form.
          // Same for `alternate_phones.<N>` / `alternate_emails.<N>` so the
          // sidebar checkmarks the bucket when any slot carries a value.
          const debtsMatch = key.match(/^debts\[\d+\]\.(.+)$/);
          if (debtsMatch) {
            keys.add(`debt.${debtsMatch[1]}`);
            return;
          }
          const altMatch = key.match(/^alternate_(phones|emails)\.\d+$/);
          if (altMatch) {
            keys.add(`alternate_${altMatch[1]}`);
            return;
          }
          keys.add(key);
        });
      } else {
        // Draft / Redux shape — flat top-level keys like `debt.0.amount`,
        // `user.display_name`, etc. Fold slotted `debt.<N>.<field>` back to
        // `debt.<field>` so checkmarks fire regardless of how many slots
        // the CSV carries.
        Object.entries(subscriber).forEach(([key, value]) => {
          if (!value || !String(value).trim() || key === 'error') {
            return;
          }
          keys.add(key);
          const slotted = key.match(/^debt\.\d+\.(.+)$/);
          if (slotted) {
            keys.add(`debt.${slotted[1]}`);
          }
          // `multi` targets (alternate_phones / alternate_emails) are stored
          // slot-indexed in Redux — fold them back to the bucket name so the
          // sidebar checks `alternate_phones` rather than `alternate_phones.0`.
          const altSlotted = key.match(/^alternate_(phones|emails)\.\d+$/);
          if (altSlotted) {
            keys.add(`alternate_${altSlotted[1]}`);
          }
        });
      }
    });

    return keys;
  }, [subscribers, apiSubscribers]);

  // residence_state is conditionally required when any subscriber's
  // country_code is US or BR (the orchestrator enforces it for those
  // countries). Flip its `required` flag dynamically so the sidebar moves
  // it from Optional to Required when the uploaded data actually triggers
  // the conditional.
  const needsResidenceState = useMemo(() => {
    const source = subscribers.length > 0 ? subscribers : (apiSubscribers ?? []);
    return source.some((sub) => {
      const country =
        (sub as { country_code?: unknown }).country_code ??
        (sub as { context?: { country_code?: unknown } }).context?.country_code;
      return requiresResidenceState(country);
    });
  }, [subscribers, apiSubscribers]);

  // Compute field status for each required variable
  const fieldStatuses = useMemo(() => {
    return DEBT_SIDEBAR_DISPLAY_VARIABLES.map((variable) => {
      const required =
        variable.name === 'residence_state' && needsResidenceState ? true : variable.required;
      return { ...variable, required, isPresent: getVariablePresence(variable, subscriberKeys) };
    });
  }, [subscriberKeys, needsResidenceState]);

  const hasMissingFields = fieldStatuses.some((f) => !f.isPresent && f.required);
  const totalCount = subscribers.filter((s) => !s.error).length;
  const hasSubscribers = totalCount > 0;

  // Show requirements view if no subscribers yet (draft mode or user deleted audience)
  if (!hasSubscribers && !csvMappings && (isDraft || hasUserDeletedAudience)) {
    return (
      <div className="flex w-full flex-col items-start gap-8 pt-8 pb-8">
        {/* Required fields list */}
        <div className="flex w-full max-w-[320px] flex-col items-start gap-3">
          <p className="text-sm leading-none font-medium text-foreground-muted">
            {t('campaigns.setup.required_fields')}
          </p>

          {DEBT_SIDEBAR_DISPLAY_VARIABLES.filter((v) => v.required).map((variable) => {
            const Icon = variable.icon;
            return (
              <div key={variable.name} className="flex w-full items-start gap-1">
                {Icon && (
                  <Icon className="mt-0.5 size-4 shrink-0 text-foreground-muted" size={16} />
                )}
                <div className="flex flex-col">
                  <span className="text-sm leading-5 font-medium text-foreground">
                    {variable.label}
                  </span>
                  {variable.conditionalRequirement && (
                    <span className="text-xs leading-4 text-foreground-muted">
                      {variable.conditionalRequirement}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {onDownloadSample && (
          <Button variant="outline-neutral" size="sm" onClick={onDownloadSample} className="mt-4">
            <DownloadIcon size={16} className="text-foreground-muted" />
            {t('campaigns.setup.download_template')}
          </Button>
        )}
      </div>
    );
  }

  // Show audience stats view when stats are available (draft or saved, unless user deleted audience)
  const statsDisplayCount = subscribers.length > 0 ? totalCount : subscriberCount;
  if (hasAudienceStats && !hasUserDeletedAudience && subscribers.length === 0 && !isDraft) {
    return (
      <div className="flex w-[500px] shrink-0 flex-col items-center gap-8 px-4 pt-8 pb-8">
        {/* Header with count */}
        <div className="flex w-80 flex-col items-start gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {t('campaigns.setup.audience_summary')}
            </span>
            <span className="text-sm font-medium text-foreground-muted">·</span>
            <div className="flex items-center gap-1">
              <UsersRoundIcon className="size-4 text-foreground-muted" />
              <span className="text-sm font-medium text-foreground">
                {statsDisplayCount.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <span className="text-sm leading-6 text-foreground-muted">
                {t('campaigns.setup.stats_emails')}
              </span>
              <span className="text-base font-medium text-foreground">
                {audienceStats.email_subscribers.toLocaleString()}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm leading-6 text-foreground-muted">
                {t('campaigns.setup.stats_phone_numbers')}
              </span>
              <span className="text-base font-medium text-foreground">
                {audienceStats.phone_subscribers.toLocaleString()}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm leading-6 text-foreground-muted">
                {t('campaigns.setup.stats_total_amount_due')}
              </span>
              <span className="text-base font-medium text-foreground">
                {formatCurrency(audienceStats.total_amount_due)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm leading-6 text-foreground-muted">
                {t('campaigns.setup.stats_variables')}
              </span>
              <span className="text-base font-medium text-foreground">
                {audienceStats.variables_count}
              </span>
            </div>
          </div>

          {/* Review audience link */}
          {onReviewAudience && (
            <button
              type="button"
              className="text-sm font-medium text-accent hover:underline"
              onClick={onReviewAudience}
            >
              {t('campaigns.setup.review_audience')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show audience summary view after CSV upload (draft mode) or saved campaign without metadata
  const displayCount = subscriberCount || totalCount;
  return (
    <div className="flex w-[500px] shrink-0 flex-col items-center gap-8 pt-8 pb-8">
      {/* Header with count */}
      <div className="flex w-[320px] flex-col items-start gap-1">
        <h3 className="overflow-hidden text-lg leading-7 font-semibold text-ellipsis text-foreground">
          {t('campaigns.setup.audience_summary')}
        </h3>
        <div className="flex items-center gap-1">
          <UsersRoundIcon className="size-4 text-foreground-muted" />
          <span className="text-sm leading-6 font-medium text-foreground">
            {displayCount.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Required fields */}
      <div className="flex w-full max-w-[320px] flex-col items-start gap-3">
        <p className="text-sm leading-none font-medium text-foreground-muted">
          {t('campaigns.setup.required_fields')}
        </p>

        {fieldStatuses
          .filter((f) => f.required)
          .map((field) => (
            <div key={field.name} className="flex w-full items-center gap-1">
              {field.isPresent ? (
                <CircleCheckIcon className="size-4 shrink-0 text-emerald-500" />
              ) : (
                <CircleAlertIcon className="size-4 shrink-0 text-destructive" />
              )}
              <span
                className={cn(
                  'text-sm leading-5 font-medium',
                  field.isPresent ? 'text-foreground-muted' : 'text-destructive'
                )}
              >
                {field.label}
              </span>
            </div>
          ))}
      </div>

      {/* Optional fields — only the ones we actually have data for. Hiding
          unmatched optional fields here matches the audience-page warning:
          we don't surface optional gaps as something the user must fix. */}
      {fieldStatuses.some((f) => !f.required && f.isPresent) && (
        <div className="flex w-full max-w-[320px] flex-col items-start gap-3">
          <p className="text-sm leading-none font-medium text-foreground-muted">
            {t('campaigns.setup.optional_fields')}
          </p>

          {fieldStatuses
            .filter((f) => !f.required && f.isPresent)
            .map((field) => (
              <div key={field.name} className="flex w-full items-center gap-1">
                <CircleCheckIcon className="size-4 shrink-0 text-emerald-500" />
                <span className="text-sm leading-5 font-medium text-foreground-muted">
                  {field.label}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Action links */}
      <div className="flex w-[320px] items-center gap-1.5">
        {isDraft ? (
          <>
            {onMatchMissingFields && (
              <button
                type="button"
                className="text-sm leading-none font-medium text-accent hover:underline"
                onClick={onMatchMissingFields}
              >
                {hasMissingFields
                  ? t('campaigns.setup.match_missing_fields')
                  : t('campaigns.setup.match_fields')}
              </button>
            )}
            {onMatchMissingFields && onReviewAudience && (
              <span className="text-sm font-medium text-foreground-muted">·</span>
            )}
            {onReviewAudience && (
              <button
                type="button"
                className="text-sm leading-none font-medium text-accent hover:underline"
                onClick={onReviewAudience}
              >
                {t('campaigns.setup.review_audience')}
              </button>
            )}
          </>
        ) : (
          onReviewAudience && (
            <button
              type="button"
              className="text-sm leading-none font-medium text-accent hover:underline"
              onClick={onReviewAudience}
            >
              {t('campaigns.setup.review_audience')}
            </button>
          )
        )}
      </div>
    </div>
  );
};
